import { useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, m } from "framer-motion";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

interface AllergiesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAllergies: string[];
  onSave: (allergies: string[]) => Promise<void>;
}

export function AllergiesModal({
  open,
  onOpenChange,
  currentAllergies,
  onSave,
}: AllergiesModalProps) {
  const { t } = useLingui();
  const [allergies, setAllergies] = useState<string[]>(currentAllergies);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addAllergy = () => {
    const trimmed = input.trim();
    if (!trimmed || allergies.includes(trimmed)) return;
    setAllergies((prev) => [...prev, trimmed]);
    setInput("");
    inputRef.current?.focus();
  };

  const removeAllergy = (index: number) => {
    setAllergies((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addAllergy();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    // Auto-commit any pending text so a user who types one allergy and taps
    // Save (without pressing Enter or +) doesn't lose the entry.
    const trimmed = input.trim();
    const finalList = trimmed && !allergies.includes(trimmed) ? [...allergies, trimmed] : allergies;
    try {
      await onSave(finalList);
      setAllergies(finalList);
      setInput("");
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => onOpenChange(false)}
      title={<Trans>Edit allergies</Trans>}
      description={<Trans>Add or remove allergies. Press Enter or tap + to add.</Trans>}
      width="md"
      ariaLabel={t`Edit allergies`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-[44px]">
            {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Input row */}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t`e.g. Penicillin, peanuts…`}
            className="h-10 flex-1"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={addAllergy}
            disabled={!input.trim()}
            className="size-10 shrink-0"
            aria-label={t`Add allergy`}
          >
            <Plus className="size-4" />
          </Button>
        </div>

        {/* Tags */}
        <div className="min-h-[60px]">
          {allergies.length === 0 ? (
            <p className="text-muted-foreground py-4 text-center text-sm">
              <Trans>No allergies added yet.</Trans>
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <AnimatePresence mode="popLayout">
                {allergies.map((allergy, i) => (
                  <m.span
                    key={allergy}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15, delay: i * 0.02 }}
                    className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 dark:bg-red-950 dark:text-red-300"
                  >
                    {allergy}
                    <button
                      type="button"
                      onClick={() => removeAllergy(i)}
                      aria-label={t`Remove allergy`}
                      className="ml-0.5 flex size-4 items-center justify-center rounded-full transition-colors hover:bg-red-200 dark:hover:bg-red-800"
                    >
                      <X className="size-3" />
                    </button>
                  </m.span>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </ResponsiveSheet>
  );
}
